import React, { useState, useEffect } from 'react';
import { User, Mail, Globe, Building, Briefcase, Shield, Check, Save, ArrowLeft, CreditCard, ExternalLink, Palette, ChevronRight } from 'lucide-react';
import { checkSubscriptionStatus, openCustomerPortal } from '../services/subscriptionService';

interface UserSettingsPageProps {
  navigateTo: (page: string) => void;
  userEmail?: string | null;
  onSettingsUpdate?: (settings: UserSettings) => void;
}

interface UserSettings {
  workEmail: string;
  displayName: string;
  language: 'en' | 'ja';
  timezone: string;
  companyName: string;
  industry: string;
  consentTerms: boolean;
  consentPrivacy: boolean;
  consentEmailNotifications: boolean;
  consentDataProcessing: boolean;
}

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Financial Services',
  'Manufacturing',
  'Retail',
  'Education',
  'Government',
  'Non-profit',
  'Media & Entertainment',
  'Real Estate',
  'Transportation',
  'Energy',
  'Consulting',
  'Legal',
  'Other'
];

const TIMEZONES = [
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'America/New_York', label: 'New York (EST)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)' }
];

const UserSettingsPage: React.FC<UserSettingsPageProps> = ({
  navigateTo,
  userEmail,
  onSettingsUpdate
}) => {
  const [settings, setSettings] = useState<UserSettings>({
    workEmail: userEmail || '',
    displayName: '',
    language: 'en',
    timezone: 'Asia/Tokyo',
    companyName: '',
    industry: '',
    consentTerms: false,
    consentPrivacy: false,
    consentEmailNotifications: false,
    consentDataProcessing: false
  });

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    has_active_subscription: boolean;
    subscription_id: string | null;
    plan_id: string | null;
    status: string;
    slides_used: number;
    slides_limit: number;
    is_read_only: boolean;
    period_end: string | null;
  } | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('advisio_user_settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Error loading user settings:', error);
      }
    }
  }, []);

  // Load subscription status on mount
  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      try {
        const status = await checkSubscriptionStatus();
        setSubscriptionStatus(status);
      } catch (error) {
        console.error('Error loading subscription status:', error);
      } finally {
        setLoadingSubscription(false);
      }
    };

    loadSubscriptionStatus();
  }, []);

  // Validation
  const validateSettings = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!settings.workEmail.trim()) {
      newErrors.workEmail = 'Work email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settings.workEmail)) {
      newErrors.workEmail = 'Please enter a valid email address';
    }

    if (!settings.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }

    if (!settings.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!settings.industry) {
      newErrors.industry = 'Please select an industry';
    }

    if (!settings.consentTerms) {
      newErrors.consentTerms = 'You must agree to the Terms of Service';
    }

    if (!settings.consentPrivacy) {
      newErrors.consentPrivacy = 'You must agree to the Privacy Policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateSettings()) {
      return;
    }

    setSaving(true);
    try {
      // Save to localStorage
      localStorage.setItem('advisio_user_settings', JSON.stringify(settings));

      // Call the callback if provided
      onSettingsUpdate?.(settings);

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: keyof UserSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const getCompanyDomain = (email: string) => {
    const match = email.match(/@(.+)$/);
    return match ? match[1] : '';
  };

  const handleOpenPortal = async () => {
    setOpeningPortal(true);
    try {
      await openCustomerPortal();
      // Redirection happens in the service, so this code won't execute
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      alert('Failed to open subscription management portal. Please try again.');
      setOpeningPortal(false);
    }
  };

  const getPlanDisplayName = (planId: string | null): string => {
    switch (planId) {
      case 'basic':
        return 'Basic Plan';
      case 'business':
        return 'Business Plan';
      case 'unlimited':
        return 'Unlimited Plan';
      default:
        return 'Unknown Plan';
    }
  };

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigateTo('chat')}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              title="Back to Chat"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">User Settings</h1>
              <p className="text-sm text-gray-600">Manage your profile and preferences</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {saved && (
              <div className="flex items-center text-green-600 text-sm">
                <Check size={16} className="mr-1" />
                Settings saved
              </div>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={16} className="mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto py-8 px-6">
        <div className="space-y-8">

          {/* Account Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-6">
              <User className="h-5 w-5 text-blue-600 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Account Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Work Email */}
              <div>
                <label htmlFor="workEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  Work Email <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 block">（勤務先メール）</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <input
                    type="email"
                    id="workEmail"
                    value={settings.workEmail}
                    onChange={(e) => updateSetting('workEmail', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.workEmail ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="your.email@company.com"
                  />
                </div>
                {settings.workEmail && (
                  <p className="mt-1 text-xs text-gray-500">
                    Company domain: {getCompanyDomain(settings.workEmail)}
                  </p>
                )}
                {errors.workEmail && (
                  <p className="mt-1 text-sm text-red-600">{errors.workEmail}</p>
                )}
              </div>

              {/* Display Name */}
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
                  Display Name <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 block">（表示名）</span>
                </label>
                <input
                  type="text"
                  id="displayName"
                  value={settings.displayName}
                  onChange={(e) => updateSetting('displayName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.displayName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Your Name"
                />
                {errors.displayName && (
                  <p className="mt-1 text-sm text-red-600">{errors.displayName}</p>
                )}
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-6">
              <Building className="h-5 w-5 text-blue-600 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Company Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Company Name */}
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="companyName"
                  value={settings.companyName}
                  onChange={(e) => updateSetting('companyName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.companyName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Your Company"
                />
                {errors.companyName && (
                  <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
                )}
              </div>

              {/* Industry */}
              <div>
                <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                  Industry <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <select
                    id="industry"
                    value={settings.industry}
                    onChange={(e) => updateSetting('industry', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.industry ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Industry</option>
                    {INDUSTRIES.map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>
                {errors.industry && (
                  <p className="mt-1 text-sm text-red-600">{errors.industry}</p>
                )}
              </div>
            </div>
          </div>

          {/* Subscription Management */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-6">
              <CreditCard className="h-5 w-5 text-blue-600 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Subscription Management</h2>
              <span className="text-xs text-gray-500 ml-2">（サブスクリプション管理）</span>
            </div>

            {loadingSubscription ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : subscriptionStatus?.has_active_subscription ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Plan
                    </label>
                    <p className="text-base font-semibold text-gray-900">
                      {getPlanDisplayName(subscriptionStatus.plan_id)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Check className="h-3 w-3 mr-1" />
                        {subscriptionStatus.status === 'active' ? 'Active' : subscriptionStatus.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Usage Progress */}
                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slide Usage
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full ${
                          subscriptionStatus.slides_limit > 0 && subscriptionStatus.slides_used / subscriptionStatus.slides_limit >= 0.9
                            ? 'bg-red-500'
                            : subscriptionStatus.slides_limit > 0 && subscriptionStatus.slides_used / subscriptionStatus.slides_limit >= 0.7
                            ? 'bg-amber-500'
                            : 'bg-blue-600'
                        }`}
                        style={{
                          width: `${
                            subscriptionStatus.slides_limit === 0
                              ? 0
                              : Math.min((subscriptionStatus.slides_used / subscriptionStatus.slides_limit) * 100, 100)
                          }%`
                        }}
                      />
                    </div>
                    <span className="text-sm text-gray-600 whitespace-nowrap">
                      {subscriptionStatus.slides_used}/{subscriptionStatus.slides_limit === 0 ? 'Unlimited' : subscriptionStatus.slides_limit}
                    </span>
                  </div>
                  {subscriptionStatus.period_end && (
                    <p className="text-xs text-gray-500 mt-1">
                      Resets on {new Date(subscriptionStatus.period_end).toLocaleDateString()}
                    </p>
                  )}
                  {subscriptionStatus.is_read_only && (
                    <p className="text-xs text-amber-600 mt-1">
                      Slide limit reached. Upgrade your plan for more slides.
                    </p>
                  )}
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={handleOpenPortal}
                    disabled={openingPortal}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {openingPortal ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Opening Portal...
                      </>
                    ) : (
                      <>
                        <ExternalLink size={16} className="mr-2" />
                        Manage Subscription
                      </>
                    )}
                  </button>
                  <p className="mt-2 text-xs text-gray-500">
                    Cancel, change plans, update payment methods, and view billing history
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">No active subscription</p>
                <button
                  onClick={() => navigateTo('pricing')}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  View Pricing Plans
                </button>
              </div>
            )}
          </div>

          {/* Slide Design */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Palette className="h-5 w-5 text-blue-600 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Slide Design</h2>
              <span className="text-xs text-gray-500 ml-2">（スライドデザイン）</span>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              カスタムデザインテーマをアップロードして、プレゼンテーションの見た目をカスタマイズできます。
            </p>
            <button
              onClick={() => navigateTo('design')}
              className="inline-flex items-center px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Palette size={16} className="mr-2" />
              Manage Design Themes
              <ChevronRight size={16} className="ml-2" />
            </button>
          </div>

          {/* Preferences */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-6">
              <Globe className="h-5 w-5 text-blue-600 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Language & Timezone</h2>
              <span className="text-xs text-gray-500 ml-2">（言語・タイムゾーン）</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Language */}
              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <select
                  id="language"
                  value={settings.language}
                  onChange={(e) => updateSetting('language', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="en">English</option>
                  <option value="ja">日本語 (Japanese)</option>
                </select>
              </div>

              {/* Timezone */}
              <div>
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  id="timezone"
                  value={settings.timezone}
                  onChange={(e) => updateSetting('timezone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {TIMEZONES.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Consent & Privacy */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-6">
              <Shield className="h-5 w-5 text-blue-600 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Consent & Privacy</h2>
              <span className="text-xs text-gray-500 ml-2">（同意）</span>
            </div>

            <div className="space-y-4">
              {/* Terms of Service */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="consentTerms"
                  checked={settings.consentTerms}
                  onChange={(e) => updateSetting('consentTerms', e.target.checked)}
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="consentTerms" className="ml-3 text-sm">
                  <span className="text-gray-900">I agree to the </span>
                  <button type="button" className="text-blue-600 hover:text-blue-700 underline">Terms of Service</button>
                  <span className="text-red-500 ml-1">*</span>
                  {errors.consentTerms && (
                    <p className="text-red-600 text-xs mt-1">{errors.consentTerms}</p>
                  )}
                </label>
              </div>

              {/* Privacy Policy */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="consentPrivacy"
                  checked={settings.consentPrivacy}
                  onChange={(e) => updateSetting('consentPrivacy', e.target.checked)}
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="consentPrivacy" className="ml-3 text-sm">
                  <span className="text-gray-900">I agree to the </span>
                  <button type="button" className="text-blue-600 hover:text-blue-700 underline">Privacy Policy</button>
                  <span className="text-red-500 ml-1">*</span>
                  {errors.consentPrivacy && (
                    <p className="text-red-600 text-xs mt-1">{errors.consentPrivacy}</p>
                  )}
                </label>
              </div>

              {/* Email Notifications */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="consentEmailNotifications"
                  checked={settings.consentEmailNotifications}
                  onChange={(e) => updateSetting('consentEmailNotifications', e.target.checked)}
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="consentEmailNotifications" className="ml-3 text-sm text-gray-900">
                  I want to receive email notifications about product updates and tips
                </label>
              </div>

              {/* Data Processing */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="consentDataProcessing"
                  checked={settings.consentDataProcessing}
                  onChange={(e) => updateSetting('consentDataProcessing', e.target.checked)}
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="consentDataProcessing" className="ml-3 text-sm text-gray-900">
                  I consent to data processing for analytics and service improvement
                </label>
              </div>
            </div>
          </div>

          {/* Save Button (Mobile) */}
          <div className="md:hidden">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full inline-flex items-center justify-center px-4 py-3 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={16} className="mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettingsPage;
